/**
 * Footer chrome link data — the per-variant footer link trees plus the
 * shared-route set the drift guard asserts. Split from `chrome-links.ts`
 * (file-size ceiling): this module owns everything footer-shaped; nav links,
 * routes, and CTAs stay in `chrome-links.ts`. Route hrefs still source from
 * `CHROME_ROUTES` so a page can never exist in one footer's data but not the
 * registry.
 */
import { CHROME_ROUTES } from './chrome-links';

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

/** How a footer link is rendered. `route` → locale-aware `<Link>`; `hash` →
 *  in-page `<a href="#...">`; `external` → new-tab `<a>`. (A `staticFile` kind
 *  existed for the build-generated `.rss` feeds; it left with them.) */
export type ChromeFooterKind = 'route' | 'hash' | 'external';

export interface ChromeFooterLink {
  /** i18n label key, resolved via `t(...)`. Variant-specific (this list is
   *  already scoped to one variant), so a plain key rather than a per-variant map. */
  readonly labelKey: string;
  /** A `CHROME_ROUTES.*` value for routes; an inline `#anchor` / URL otherwise. */
  readonly href: string;
  readonly kind: ChromeFooterKind;
}

export interface ChromeFooterGroup {
  /** Stable, locale-independent slug for React keys + `data-footer-column`. */
  readonly slug: string;
  /** i18n key for the column heading. */
  readonly headingKey: string;
  readonly links: readonly ChromeFooterLink[];
}

/**
 * The two footers' grouped link data, one tree per variant. The trees are kept
 * separate on purpose: the footers legitimately group the same routes into
 * different columns and order their columns differently, so a single shared
 * tree cannot reproduce both without changing one footer's appearance.
 * Unification is at the DATA level instead: every route link below sources its
 * href from `CHROME_ROUTES`, and a drift-guard test asserts the routes common
 * to both trees render in both footers.
 *
 * **The legal documents are deliberately NOT enumerated here.** The footer
 * links the `/legal` index, which links every legal document (terms, privacy,
 * DPA, refunds, AI disclosure, compliance, accessibility) — one footer entry
 * instead of a nine-row column. The one exception is the cookie policy, which
 * keeps its own footer link: it is the ePrivacy surface a visitor looks for by
 * name, next to the consent banner that references it.
 */
export const FOOTER_LINK_GROUPS: {
  readonly landing: readonly ChromeFooterGroup[];
  readonly site: readonly ChromeFooterGroup[];
} = {
  // Dark landing footer. Landing-only in-page anchors (`#...`) and not-yet-built
  // pages (`#top` placeholders) sit alongside the shared routes.
  landing: [
    {
      slug: 'product',
      headingKey: 'landing.final.foot.colProduct',
      links: [
        { labelKey: 'landing.final.foot.how', href: CHROME_ROUTES.howItWorks, kind: 'route' },
        { labelKey: 'landing.final.foot.surfaces', href: '#surfaces', kind: 'hash' },
        { labelKey: 'landing.final.foot.mcp', href: '#mcp', kind: 'hash' },
        { labelKey: 'landing.final.foot.theming', href: '#platform', kind: 'hash' },
        { labelKey: 'landing.final.foot.features', href: CHROME_ROUTES.features, kind: 'route' },
        { labelKey: 'landing.final.foot.compare', href: CHROME_ROUTES.compare, kind: 'route' },
        { labelKey: 'landing.final.foot.pricing', href: CHROME_ROUTES.pricing, kind: 'route' },
      ],
    },
    {
      slug: 'resources',
      headingKey: 'landing.final.foot.colResources',
      links: [
        { labelKey: 'landing.final.foot.docs', href: CHROME_ROUTES.docs, kind: 'route' },
        { labelKey: 'landing.final.foot.api', href: CHROME_ROUTES.docsReference, kind: 'route' },
        { labelKey: 'landing.final.foot.guides', href: CHROME_ROUTES.docsGuides, kind: 'route' },
        { labelKey: 'landing.final.foot.sdks', href: CHROME_ROUTES.docsSdks, kind: 'route' },
        {
          labelKey: 'landing.final.foot.benchmarks',
          href: CHROME_ROUTES.benchmarks,
          kind: 'route',
        },
      ],
    },
    {
      slug: 'company',
      headingKey: 'landing.final.foot.colCompany',
      links: [
        { labelKey: 'landing.final.foot.about', href: CHROME_ROUTES.about, kind: 'route' },
        { labelKey: 'landing.final.foot.contact', href: CHROME_ROUTES.contact, kind: 'route' },
        { labelKey: 'landing.final.foot.status', href: CHROME_ROUTES.status, kind: 'route' },
        { labelKey: 'landing.final.foot.onePagers', href: CHROME_ROUTES.onePagers, kind: 'route' },
      ],
    },
    {
      slug: 'trust',
      headingKey: 'landing.final.foot.colTrust',
      links: [
        {
          labelKey: 'landing.final.foot.security',
          href: CHROME_ROUTES.trustSecurity,
          kind: 'route',
        },
        { labelKey: 'landing.final.foot.legal', href: CHROME_ROUTES.legal, kind: 'route' },
      ],
    },
  ],
  // Light site-wide footer (the more complete, launch footer). Only routes that
  // exist or are clearly scheduled.
  site: [
    {
      slug: 'product',
      headingKey: 'footer.columnProduct',
      links: [
        { labelKey: 'footer.linkFeatures', href: CHROME_ROUTES.features, kind: 'route' },
        { labelKey: 'footer.linkHowItWorks', href: CHROME_ROUTES.howItWorks, kind: 'route' },
        { labelKey: 'footer.linkPricing', href: CHROME_ROUTES.pricing, kind: 'route' },
        { labelKey: 'footer.linkCompare', href: CHROME_ROUTES.compare, kind: 'route' },
        { labelKey: 'footer.linkBuildVsBuy', href: CHROME_ROUTES.buildVsBuy, kind: 'route' },
      ],
    },
    {
      slug: 'trust',
      headingKey: 'footer.columnTrust',
      links: [
        { labelKey: 'footer.linkTrust', href: CHROME_ROUTES.trust, kind: 'route' },
        { labelKey: 'footer.linkSecurity', href: CHROME_ROUTES.trustSecurity, kind: 'route' },
        { labelKey: 'footer.linkLegal', href: CHROME_ROUTES.legal, kind: 'route' },
        { labelKey: 'footer.linkCookies', href: CHROME_ROUTES.cookies, kind: 'route' },
      ],
    },
    {
      slug: 'resources',
      headingKey: 'footer.columnResources',
      links: [
        { labelKey: 'footer.linkDocs', href: CHROME_ROUTES.docs, kind: 'route' },
        { labelKey: 'footer.linkBenchmarks', href: CHROME_ROUTES.benchmarks, kind: 'route' },
        { labelKey: 'footer.linkOnePagers', href: CHROME_ROUTES.onePagers, kind: 'route' },
      ],
    },
    {
      slug: 'company',
      headingKey: 'footer.columnCompany',
      links: [
        { labelKey: 'footer.linkAbout', href: CHROME_ROUTES.about, kind: 'route' },
        { labelKey: 'footer.linkTalkToSales', href: CHROME_ROUTES.contact, kind: 'route' },
      ],
    },
  ],
};

/**
 * The set of full-page route hrefs that appear in BOTH footers — the drift
 * guard's subject. Computed from `FOOTER_LINK_GROUPS` so it stays correct as
 * the footers evolve (add a route to both trees → it joins this set → the drift
 * test starts asserting it renders in both).
 */
export const SHARED_FOOTER_ROUTE_HREFS: readonly string[] = (() => {
  const routeHrefs = (groups: readonly ChromeFooterGroup[]): Set<string> =>
    new Set(
      groups.flatMap((group) =>
        group.links.filter((link) => link.kind === 'route').map((link) => link.href),
      ),
    );
  const landing = routeHrefs(FOOTER_LINK_GROUPS.landing);
  const site = routeHrefs(FOOTER_LINK_GROUPS.site);
  return [...landing].filter((href) => site.has(href)).sort();
})();
